import React, { useCallback, useMemo, useState } from 'react';

import { CalendarIcon, ClockIcon, FileTextIcon } from '@radix-ui/react-icons';
import { Flex, Label, Radio, Select, Text } from '@raystack/apsara';
import { createFormattedAreaEl } from '@studio/map';
import * as _ from 'lodash';
import moment from 'moment';
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Line,
  ReferenceLine,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';

import { ScrollView } from '@src/components/PixxelUIKit/ScrollView';
import * as Skeleton from '@src/components/PixxelUIKit/Skeleton';
import { For, Show } from '@src/hoc';
import {
  CROP_PHEN_END_DATE_INPUT_NAME,
  CROP_PHEN_GRAPH_NAME,
  CROP_PHEN_START_DATE_INPUT_NAME
} from '@src/utils/constants/MODE_INFERENCE_DEFAULTS';
import { getUSTimeFormat } from '@src/utils/dateUtils';

import CropPhenCycleStatsSection from './CropPhenCycleStatsSection';

import styles from './styles.module.css';

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    const { payload: data } = payload[0];
    return (
      <Flex
        direction='column'
        gap='small'
        align='start'
        className={styles.graphTooltipWrapper}
      >
        <Text size={2} weight={500} variant='primary'>
          {label}
        </Text>
        <Flex gap='medium' align='stretch'>
          <Text size={2} weight={500} variant='secondary'>
            Mean NDVI
          </Text>{' '}
          <Text size={2} weight={500} variant='primary'>
            {data.ndvi}
          </Text>
        </Flex>
      </Flex>
    );
  }

  return null;
};

export const StrokeLine = ({ color }) => {
  return (
    <svg height='24' width='24'>
      <line
        x1='0'
        y1='12'
        x2='100%'
        y2='12'
        style={{
          stroke: color,
          strokeWidth: 2,
          strokeDasharray: '3,3'
        }}
      />
    </svg>
  );
};

const SelectCycleStats = ({ cycle = {} }) => {
  const duration = useMemo(() => {
    const { SOS = '', EOS = '' } = cycle;
    // Create Date objects from the ISO strings
    const startDate = new Date(SOS);
    const endDate = new Date(EOS);
    // Calculate the difference in milliseconds
    const timeDifferenceMS = endDate - startDate;
    // Convert milliseconds to days
    const duration = Math.floor(timeDifferenceMS / (1000 * 60 * 60 * 24));
    if (SOS && EOS) return duration;

    return 0;
  }, [cycle]);

  const dateStats = useMemo(() => {
    const { SOS = '', EOS = '', 'Peak date': peakDate = '' } = cycle;

    return [
      {
        label: 'Season Starting',
        color: 'var(--rs-color-viz-blue-11)',
        value: SOS ? getUSTimeFormat(SOS) : ''
      },
      {
        label: 'Peak Season',
        color: 'var(--rs-color-viz-jade-11)',
        value: peakDate ? getUSTimeFormat(peakDate) : ''
      },
      {
        label: 'End of Season',
        color: 'var(--rs-color-foreground-danger-primary)',
        value: EOS ? getUSTimeFormat(EOS) : ''
      }
    ];
  }, [cycle]);

  return (
    <Flex
      direction='column'
      align='stretch'
      style={{ width: '100%', marginTop: 'var(--rs-space-4)' }}
    >
      <Flex
        align='stretch'
        justify='between'
        style={{
          padding: 'var(--rs-space-5) 0px',
          borderBottom: '1px solid var(--rs-color-border-base-primary)'
        }}
      >
        <Flex align='center' gap='extra-small'>
          <ClockIcon />
          <Text size={4} weight={400} variant='secondary'>
            Cycle Duration
          </Text>
        </Flex>
        <Text size={4} weight={400}>
          {duration > 0 ? `${duration} days` : 'NA'}
        </Text>
      </Flex>
      <For each={dateStats}>
        {stat => (
          <Flex
            align='stretch'
            justify='between'
            style={{
              padding: 'var(--rs-space-5) 0px',
              borderBottom: '1px solid var(--rs-color-border-base-primary)'
            }}
          >
            <Flex gap='extra-small' align='center'>
              <StrokeLine color={stat.color} />{' '}
              <Text size={4} weight={400} variant='secondary'>
                {stat.label}
              </Text>
            </Flex>
            <Text size={4} weight={400}>
              {stat.value}
            </Text>
          </Flex>
        )}
      </For>
    </Flex>
  );
};

export const CropPhenologyGraph = ({ activeFarm = {} }) => {
  const [isHovered, setIsHovered] = useState('');
  const [selectedCycle, setSelectedCycle] = useState({});

  const getCycleColor = useCallback(
    cycle => {
      if (selectedCycle.Cycle === cycle.Cycle)
        return {
          value: 'var(--rs-color-viz-orange-8)',
          opacity: 1
        };

      if (isHovered === cycle.Cycle)
        return {
          value: 'var(--rs-color-viz-orange-6)',
          opacity: 0.5
        };

      return {
        value: 'var(--rs-color-background-neutral-secondary-hover)',
        opacity: 0.5
      };
    },
    [selectedCycle, isHovered]
  );

  const activeGraph = useMemo(() => {
    setSelectedCycle({});
    const datelist = activeFarm['Datetime'];
    const smoothenedNDVI = activeFarm['Smoothened NDVI'];
    const cycles = activeFarm.cycles.map(cycle => {
      const sos =
        cycle['SOS'] === null
          ? new Date(datelist[0]).toISOString()
          : cycle['SOS'];

      const eos =
        cycle['EOS'] === null
          ? new Date(datelist.at(-1)).toISOString()
          : cycle['EOS'];

      return {
        ...cycle,
        SOS: cycle['SOS'],
        EOS: cycle['EOS'],
        modifiedSOS: sos,
        modifiedEOS: eos
      };
    });
    const graphData = datelist.map((date, i) => {
      const data = {
        date: getUSTimeFormat(date),
        ndvi: smoothenedNDVI[i]
      };

      cycles.forEach(cycle => {
        const start = moment(cycle.modifiedSOS);
        const end = moment(cycle.modifiedEOS);
        const currentDate = moment(date);
        if (
          currentDate.isSameOrAfter(start, 'day') &&
          currentDate.isSameOrBefore(end, 'day')
        ) {
          data[cycle.Cycle] = smoothenedNDVI[i];
        }
      });
      return data;
    });

    return {
      data: graphData,
      cycles: cycles
    };
  }, [activeFarm]);

  return (
    <Show when={!_.isEmpty(activeGraph) && activeGraph?.data.length > 0}>
      <ComposedChart
        width={370}
        height={350}
        data={activeGraph?.data || []}
        margin={{
          top: 0,
          right: 0,
          left: -10
        }}
      >
        <XAxis dataKey='date' fontSize={11} />
        <YAxis
          fontSize={12}
          dataKey='ndvi'
          domain={[0, 1]}
          label={{
            value: 'Mean NDVI values',
            style: { textAnchor: 'middle', fontSize: '11px' },
            angle: -90,
            position: 'left',
            offset: -18
          }}
        />
        <CartesianGrid
          strokeDasharray='3 3'
          stroke='var(--rs-color-foreground-base-secondary)'
          vertical={false}
          opacity={0.4}
        />
        <Tooltip content={<CustomTooltip />} cursor={{ fill: 'transparent' }} />

        {activeGraph.cycles.map((cycle, i) => (
          <Area
            type='monotone'
            dataKey={cycle.Cycle}
            key={i}
            fill={getCycleColor(cycle).value}
            fillOpacity={getCycleColor(cycle).opacity}
            stroke={getCycleColor(cycle).value}
            data-test-id='click-graph-btn'
            onClick={() => setSelectedCycle(cycle)}
            onMouseEnter={() => setIsHovered(cycle.Cycle)}
            onMouseLeave={() => setIsHovered('')}
            style={{ cursor: 'pointer' }}
          />
        ))}

        <Line
          type='monotone'
          dataKey='ndvi'
          stroke={'var(--rs-color-background-neutral-tertiary)'}
          dot={false}
        />

        {/* Reference line for Start of season */}
        {activeGraph?.cycles?.map((item, i) => {
          if (item.SOS !== null) {
            return (
              <ReferenceLine
                strokeWidth={2}
                x={getUSTimeFormat(item.SOS)}
                key={`${i}-sos`}
                stroke='var(--rs-color-viz-blue-11)'
                strokeDasharray='3 3'
              />
            );
          }
        }) || null}

        {/* Refernce line for end of season */}
        {activeGraph?.cycles?.map((item, i) => {
          if (item.EOS !== null) {
            return (
              <ReferenceLine
                strokeWidth={2}
                x={getUSTimeFormat(item.EOS)}
                key={`${i}-eos`}
                stroke='var(--rs-color-foreground-danger-primary)'
                strokeDasharray='3 3'
              />
            );
          }
        }) || null}

        {/* reference line for peak date */}

        {activeGraph?.cycles?.map((item, i) => {
          if (item['Peak date'] !== null) {
            return (
              <ReferenceLine
                strokeWidth={2}
                x={getUSTimeFormat(item['Peak date'])}
                key={`${i}-peak-date`}
                stroke='var(--rs-color-viz-jade-11)'
                strokeDasharray='3 3'
              />
            );
          }
        }) || null}
      </ComposedChart>
      <Flex
        justify='between'
        align='start'
        style={{
          flexFlow: 'row wrap'
        }}
      >
        <Flex gap='small' align='center'>
          <StrokeLine color={'var(--rs-color-viz-blue-11)'} />
          <Text>Start of season</Text>
        </Flex>
        <Flex gap='small' align='center'>
          <StrokeLine color={'var(--rs-color-viz-jade-11)'} />
          <Text>Peak of season</Text>
        </Flex>
        <Flex gap='small' align='center'>
          <StrokeLine color={'var(--rs-color-foreground-danger-primary)'} />
          <Text>End of season</Text>
        </Flex>
      </Flex>
      <Show when={!_.isEmpty(selectedCycle)}>
        <SelectCycleStats cycle={selectedCycle} />
      </Show>
    </Show>
  );
};

const NDVIGraphSection = ({
  activeFarm,
  onActiveFarmChange,
  jsonLoader,
  graphData
}) => {
  return (
    <>
      <div className={styles.headingDiv2}>
        <Text className={styles.title} size={4}>
          NDVI graph
        </Text>
        <Select value={activeFarm.farm_name} onValueChange={onActiveFarmChange}>
          <Select.Trigger className={styles.paramSelect}>
            <Select.Value placeholder='Select a farm' />
          </Select.Trigger>
          <Select.Content style={{ zIndex: 10000 }}>
            <Select.Group>
              <ScrollView height='200px'>
                <For each={Object.keys(graphData[CROP_PHEN_GRAPH_NAME] ?? {})}>
                  {farm_id => (
                    <Select.Item value={farm_id} key={farm_id}>
                      <Flex>
                        Farm {farm_id}
                        <Text
                          weight={400}
                          size={2}
                          variant='tertiary'
                          style={{ display: 'inline-flex' }}
                        >
                          (
                          {createFormattedAreaEl(
                            graphData[CROP_PHEN_GRAPH_NAME][farm_id]['Area'] /
                              1e6
                          )}
                          )
                        </Text>
                      </Flex>
                    </Select.Item>
                  )}
                </For>
              </ScrollView>
            </Select.Group>
          </Select.Content>
        </Select>
      </div>
      <Text size={2} weight={400} variant='secondary'>
        Click on the graph to view more data
      </Text>
      <ScrollView>
        {jsonLoader ? (
          <Skeleton.TileList />
        ) : (
          <Show when={!_.isEmpty(activeFarm)}>
            <CropPhenologyGraph activeFarm={activeFarm} />
          </Show>
        )}
      </ScrollView>
    </>
  );
};

NDVIGraphSection.displayName = 'NDVIGraphSection';

export const CYCLE_OPTIONS = {
  CROP_CYCLE: 'crop-cycle',
  CYCLE_STATS: 'cycle-stats'
};
const radioGroups = [
  {
    id: 'cycle-stats',
    label: 'Cycle Statistics',
    value: CYCLE_OPTIONS.CYCLE_STATS
  },
  {
    id: 'crop-cycle',
    label: 'Crop cycle',
    value: CYCLE_OPTIONS.CROP_CYCLE
  }
];

export const CropPhenCommon = React.forwardRef(
  (
    {
      inputs,
      graphData,
      activeFarm,
      jsonLoader,
      viewMode,
      setViewMode,
      focusOnFarm,
      setHighlightedFarms,
      setSeasonMode,
      setHoverId
    },
    ref
  ) => {
    const startDate =
      inputs && inputs.length > 0
        ? inputs.filter(i => i.name === CROP_PHEN_START_DATE_INPUT_NAME)[0]
            .value
        : '';
    const endDate =
      inputs && inputs.length > 0
        ? inputs.filter(i => i.name === CROP_PHEN_END_DATE_INPUT_NAME)[0].value
        : '';

    return (
      <>
        <div className={styles.headingDiv}>
          <Text className={styles.title} size={4}>
            Farm boundary
          </Text>
          <div className={styles.inputJson}>
            <FileTextIcon /> <Text>Farm boundary.geojson</Text>
          </div>
        </div>
        <div className={styles.headingDiv}>
          <Text className={styles.title} size={4}>
            Timeframe
          </Text>
          <Text className={styles.inputDate} size={4}>
            <CalendarIcon className={styles.dateIcon} />
            {getUSTimeFormat(startDate)} - {getUSTimeFormat(endDate)}
          </Text>
        </div>
        <Radio value={viewMode} onValueChange={value => setViewMode(value)}>
          <Flex justify='start' align='center' gap='large'>
            <For each={radioGroups}>
              {d => (
                <Flex gap='small' align='center' key={d.id}>
                  <Radio.Item
                    id={d.id}
                    value={d.value}
                    data-test-id='crop-monitoring-stats-option'
                  />
                  <Label htmlFor={d.id} size='medium'>
                    {d.label}
                  </Label>
                </Flex>
              )}
            </For>
          </Flex>
        </Radio>
        <Show when={viewMode === CYCLE_OPTIONS.CYCLE_STATS}>
          <CropPhenCycleStatsSection
            vectorData={graphData[CROP_PHEN_GRAPH_NAME] ?? {}}
            loader={jsonLoader}
            focusOnFarm={focusOnFarm}
            setHighlightedFarms={setHighlightedFarms}
            setSeasonMode={setSeasonMode}
            setHoverId={setHoverId}
          />
        </Show>
        <Show when={viewMode === CYCLE_OPTIONS.CROP_CYCLE}>
          <NDVIGraphSection
            graphData={graphData}
            activeFarm={activeFarm}
            onActiveFarmChange={value => focusOnFarm(value)}
            jsonLoader={jsonLoader}
            ref={ref}
          />
        </Show>
      </>
    );
  }
);

CropPhenCommon.displayName = 'CropPhenCommon';
